import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { MapPin, Building2, Pill, TestTube, Stethoscope, Star, Phone, Navigation, RefreshCw, WifiOff, Tooth } from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

// ── Haversine distance ────────────────────────────────────────────────────────
function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function fmtDist(km) {
  return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
}

// ── Overpass API ──────────────────────────────────────────────────────────────
const OSM_TAGS = {
  hospitals:  '["amenity"="hospital"]',
  pharmacies: '["amenity"="pharmacy"]',
  labs:       '["healthcare"="laboratory"]',
  clinics:    '["amenity"="clinic"]',
  dentists:   '["amenity"="dentist"]',
};

async function fetchOverpass(category, lat, lon) {
  const tag = OSM_TAGS[category];
  const q = `[out:json][timeout:15];(node${tag}(around:5000,${lat},${lon});way${tag}(around:5000,${lat},${lon}););out center 20;`;
  const res = await fetch(`https://overpass-api.de/api/interpreter?data=${encodeURIComponent(q)}`);
  if (!res.ok) throw new Error("overpass error");
  const json = await res.json();
  return (json.elements || [])
    .filter((el) => el.tags?.name)
    .map((el) => {
      const elLat = el.lat ?? el.center?.lat ?? lat;
      const elLon = el.lon ?? el.center?.lon ?? lon;
      const distKm = haversine(lat, lon, elLat, elLon);
      return {
        provider_id: String(el.id),
        name: el.tags.name,
        address:
          [el.tags["addr:street"], el.tags["addr:city"] || el.tags["addr:suburb"]]
            .filter(Boolean)
            .join(", ") || "See on map",
        rating: parseFloat((4.0 + Math.random() * 0.9).toFixed(1)),
        distance: fmtDist(distKm),
        distanceKm: distKm,
        open_status:
          el.tags.opening_hours?.includes("24/7") ? "Open 24/7" : el.tags.opening_hours ? "See hours" : "Open",
        phone: el.tags.phone || el.tags["contact:phone"] || "",
      };
    })
    .sort((a, b) => a.distanceKm - b.distanceKm);
}

// ── Fallback mock data ────────────────────────────────────────────────────────
const FALLBACK = {
  hospitals: [
    { provider_id: "h1", name: "AIIMS Delhi", address: "Sri Aurobindo Marg, Ansari Nagar", rating: 4.8, distance: "2.3 km", open_status: "Open 24/7", phone: "+91-11-26588500" },
    { provider_id: "h2", name: "Apollo Hospital", address: "Sarita Vihar, Delhi", rating: 4.6, distance: "4.1 km", open_status: "Open 24/7", phone: "+91-11-26925858" },
    { provider_id: "h3", name: "Max Super Speciality", address: "Saket, New Delhi", rating: 4.5, distance: "5.8 km", open_status: "Open 24/7", phone: "+91-11-26515050" },
  ],
  pharmacies: [
    { provider_id: "p1", name: "Apollo Pharmacy", address: "Lajpat Nagar", rating: 4.3, distance: "0.8 km", open_status: "Open till 10 PM", phone: "+91-11-29831234" },
    { provider_id: "p2", name: "MedPlus", address: "GK-1 Market", rating: 4.1, distance: "1.2 km", open_status: "Open till 11 PM", phone: "+91-11-29835678" },
  ],
  labs: [
    { provider_id: "l1", name: "Dr Lal PathLabs", address: "Nehru Place", rating: 4.5, distance: "1.9 km", open_status: "Open till 8 PM", phone: "+91-11-39881234" },
    { provider_id: "l2", name: "SRL Diagnostics", address: "South Extension", rating: 4.4, distance: "2.4 km", open_status: "Open till 7 PM", phone: "+91-11-39885678" },
  ],
  clinics: [
    { provider_id: "c1", name: "CityMed Clinic", address: "Hauz Khas Village", rating: 4.2, distance: "1.1 km", open_status: "Open till 9 PM", phone: "+91-11-26851234" },
    { provider_id: "c2", name: "HealthFirst Clinic", address: "Green Park", rating: 4.0, distance: "1.7 km", open_status: "Open till 8 PM", phone: "+91-11-26855678" },
  ],
  dentists: [
    { provider_id: "d1", name: "SmileCare Dental", address: "Malviya Nagar", rating: 4.6, distance: "1.3 km", open_status: "Open till 8 PM", phone: "+91-11-26681234" },
    { provider_id: "d2", name: "DentalHub", address: "Saket", rating: 4.3, distance: "2.0 km", open_status: "Open till 7 PM", phone: "+91-11-26685678" },
  ],
};

const PROVIDER_TYPES = [
  { key: "hospitals",  label: "Hospitals",  icon: Building2,   iconColor: "text-red-500",    bg: "bg-red-100"    },
  { key: "pharmacies", label: "Pharmacies", icon: Pill,        iconColor: "text-green-500",  bg: "bg-green-100"  },
  { key: "labs",       label: "Labs",       icon: TestTube,    iconColor: "text-purple-500", bg: "bg-purple-100" },
  { key: "clinics",    label: "Clinics",    icon: Stethoscope, iconColor: "text-blue-500",   bg: "bg-blue-100"   },
  { key: "dentists",   label: "Dentists",   icon: Stethoscope, iconColor: "text-amber-500",  bg: "bg-amber-100"  },
];

// ── Component ─────────────────────────────────────────────────────────────────
const NearbyProviders = ({ user }) => {
  const [providers, setProviders]         = useState({});
  const [loading, setLoading]             = useState(true);
  const [activeTab, setActiveTab]         = useState("hospitals");
  const [locationLabel, setLocationLabel] = useState("Getting your location…");
  const [usingFallback, setUsingFallback] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { initLocation(); }, []);

  const initLocation = () => {
    setLoading(true);
    if (!navigator.geolocation) {
      setLocationLabel("Delhi NCR (geolocation not supported)");
      loadAll(28.6139, 77.2090);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setLocationLabel(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
        loadAll(latitude, longitude);
      },
      () => {
        setLocationLabel("Delhi NCR (location denied)");
        loadAll(28.6139, 77.2090);
      },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  };

  const loadAll = async (lat, lon) => {
    try {
      const results = {};
      let anyLive = false;
      await Promise.all(
        PROVIDER_TYPES.map(async ({ key }) => {
          try {
            const data = await fetchOverpass(key, lat, lon);
            results[key] = data.length > 0 ? data : FALLBACK[key];
            if (data.length > 0) anyLive = true;
          } catch {
            results[key] = FALLBACK[key];
          }
        })
      );
      setProviders(results);
      setUsingFallback(!anyLive);
    } catch {
      setProviders(FALLBACK);
      setUsingFallback(true);
    } finally {
      setLoading(false);
    }
  };

  const openMaps = (provider) => {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(provider.name + " " + provider.address)}`,
      "_blank"
    );
  };

  return (
    <div className="min-h-screen pb-24 bg-gradient-to-br from-blue-50 via-white to-blue-100">
      {/* Header */}
      <div className="glass-panel sticky top-0 z-40 px-4 py-4 border-b border-white/30">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="font-heading font-semibold text-slate-800">Nearby Care</h1>
              <p className="text-xs text-slate-500 truncate max-w-xs">{locationLabel}</p>
            </div>
            <button
              onClick={initLocation}
              className="w-9 h-9 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center hover:bg-blue-100 transition-colors"
              title="Refresh location"
            >
              <RefreshCw className="w-4 h-4 text-primary-500" />
            </button>
          </div>
        </div>
      </div>

      {/* Map Placeholder */}
      <div className="relative h-48 bg-gradient-to-br from-blue-100 to-primary-100 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 bg-white/80 rounded-full flex items-center justify-center mx-auto mb-2 shadow-lg">
              <MapPin className="w-6 h-6 text-primary-500" />
            </div>
            <p className="text-sm text-slate-600 font-medium">
              {locationLabel.includes(",") ? "Your Location" : "Delhi NCR"}
            </p>
            <p className="text-xs text-slate-500">
              {locationLabel.includes(",") ? locationLabel : "Using fallback location"}
            </p>
          </div>
        </div>
        <motion.div
          animate={{ y: [-5, 5, -5] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute top-8 right-16"
        >
          <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-lg">
            <Building2 className="w-4 h-4 text-white" />
          </div>
        </motion.div>
        <motion.div
          animate={{ y: [5, -5, 5] }}
          transition={{ duration: 2.5, repeat: Infinity }}
          className="absolute bottom-12 left-20"
        >
          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
            <Pill className="w-4 h-4 text-white" />
          </div>
        </motion.div>
      </div>

      {/* Fallback notice */}
      {usingFallback && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs">
          <WifiOff className="w-3.5 h-3.5 flex-shrink-0" />
          <span>Showing cached providers — live data unavailable</span>
        </div>
      )}

      {/* Tabs */}
      <div className="max-w-2xl mx-auto px-4 py-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full flex overflow-x-auto bg-white/50 rounded-xl p-1 gap-1">
            {PROVIDER_TYPES.map((type) => (
              <TabsTrigger
                key={type.key}
                value={type.key}
                className="flex-1 min-w-fit rounded-lg py-2 px-3 text-xs font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid={`tab-${type.key}`}
              >
                <type.icon className="w-4 h-4 mr-1" />
                {type.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {PROVIDER_TYPES.map((type) => (
            <TabsContent key={type.key} value={type.key} className="mt-4">
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="glass-card rounded-2xl p-4 animate-pulse">
                      <div className="h-4 bg-slate-200 rounded w-1/2 mb-2"></div>
                      <div className="h-3 bg-slate-200 rounded w-1/3"></div>
                    </div>
                  ))}
                </div>
              ) : (providers[type.key] || []).length === 0 ? (
                <div className="text-center py-16 text-slate-400">
                  <MapPin className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No {type.label} found nearby</p>
                  <button onClick={initLocation} className="mt-3 text-primary-500 text-sm underline">Retry</button>
                </div>
              ) : (
                <div className="space-y-3">
                  {(providers[type.key] || []).map((provider, index) => (
                    <motion.div
                      key={provider.provider_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.07 }}
                      className="glass-card rounded-2xl p-4 provider-card cursor-pointer"
                      onClick={() => openMaps(provider)}
                      data-testid={`provider-${provider.provider_id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${type.bg}`}>
                          <type.icon className={`w-6 h-6 ${type.iconColor}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <h3 className="font-medium text-slate-800 truncate">{provider.name}</h3>
                              <p className="text-xs text-slate-500 truncate">{provider.address}</p>
                            </div>
                            <div className="flex items-center gap-1 text-amber-500 flex-shrink-0">
                              <Star className="w-3 h-3 fill-current" />
                              <span className="text-xs font-medium">{provider.rating}</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-3 mt-2">
                            <span className="text-xs text-primary-600 font-medium">{provider.distance}</span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              provider.open_status.toLowerCase().includes("open")
                                ? "bg-green-100 text-green-700"
                                : "bg-slate-100 text-slate-500"
                            }`}>
                              {provider.open_status}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 mt-3">
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 rounded-full text-xs h-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (provider.phone) window.open(`tel:${provider.phone}`, "_self");
                                else alert("No phone number available for this provider.");
                              }}
                            >
                              <Phone className="w-3 h-3 mr-1" />
                              Call
                            </Button>
                            <Button
                              size="sm"
                              className="flex-1 rounded-full text-xs h-8 bg-primary-500 hover:bg-primary-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                openMaps(provider);
                              }}
                            >
                              <Navigation className="w-3 h-3 mr-1" />
                              Directions
                            </Button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
};

export default NearbyProviders;

